import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Accordion, DownloadDocs, Button, Notification } from "@weirgroup/weir-dls";
import { GroupFormField } from "./input-fields";
import { Card } from '../../../components/Card/card';
import { BucketDataType, DownloadParamsType, GroupedReplacementPartType, ReplacementPartType } from "../../../types/bucketTypes";
import { useTranslation } from "react-i18next";
import { ADAPTERS, OTHERS, POINTS } from "../../../constants/bucket.contants";

import assetsApi from '../../../api/assets.api';
import { useDownload } from "../../../hooks/use-download";
import { ToastDataType } from "../../../types/toast.type";
import { useDarkMode } from "../../../hooks/use-darkmode";
import "../styles.scss"
import { useDeletePartAPI, useGetAssetAPI } from "../../../hooks/use-asset-api";
import { groupReplacementParts } from "../../../utils/replacement-parts.util";
import { LoadingSpinner } from "../../loading-spinner";

interface Props {
  selectedAsset: BucketDataType;
  setShowBillOfMaterial?: Dispatch<SetStateAction<boolean>>;
  groupedReplacementParts: GroupedReplacementPartType;
  toggleBillOfMaterial?: (arg1:boolean, arg2:any) =>void;
  setEditBill?: Dispatch<SetStateAction<any>>;
  isGuest: boolean;
}

export const ReplacementParts = (props: Props) => {
  const {
    selectedAsset,
    setShowBillOfMaterial,
    groupedReplacementParts,
    toggleBillOfMaterial,
    setEditBill,
    isGuest,
  } = props;
  const { t } = useTranslation();
  const [accordianContent, setAccordianContent] = useState<any>();
  const [points, setPoints] = useState<ReplacementPartType[]>(groupedReplacementParts.POINTS || []);
  const [adapters, setAdapters] = useState<ReplacementPartType[]>(groupedReplacementParts.ADAPTERS || []);
  const [others, setOthers] = useState<ReplacementPartType[]>(groupedReplacementParts.OTHERS || []);
  const [nonEsco, setNonEsco] = useState<ReplacementPartType[]>(groupedReplacementParts.NON_ESCO || []);
  const [isAddingToList, setIsAddingToList] = useState<boolean>(false);
  const { isLoading, startDownload } = useDownload();
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastData, setToastData] = useState<any>();
  const { darkMode } = useDarkMode();
  const getAssetAPIHook = useGetAssetAPI(selectedAsset.asset_id);
  const deletePartAPIHook = useDeletePartAPI(selectedAsset.asset_id);
  const renderToastMessage = (toastData: ToastDataType) => {
    setShowToast(true);
    setToastData(toastData);
  };
  const handlePointsDeleted = (deletedPartId: number) => {
    setPoints(prev => prev.filter(part => part.part_id !== deletedPartId));
    if (groupedReplacementParts.POINTS) {
      groupedReplacementParts.POINTS = groupedReplacementParts.POINTS.filter(part => part.part_id !== deletedPartId);
    }
  };
  const handleAdaptersDeleted = (deletedPartId: number) => {
    setAdapters(prev => prev.filter(part => part.part_id !== deletedPartId));
    if (groupedReplacementParts.ADAPTERS) {
      groupedReplacementParts.ADAPTERS = groupedReplacementParts.ADAPTERS.filter(part => part.part_id !== deletedPartId);
    }
  };
  const handleOthersDeleted = (deletedPartId: number) => {
    setOthers(prev => prev.filter(part => part.part_id !== deletedPartId));
    if (groupedReplacementParts.OTHERS) {
      groupedReplacementParts.OTHERS = groupedReplacementParts.OTHERS.filter(part => part.part_id !== deletedPartId);
    }
  };
  const handleNonEscoDeleted = (deletedPartId: number) => {
    setNonEsco(prev => prev.filter(part => part.part_id !== deletedPartId));
    if (groupedReplacementParts.NON_ESCO) {
      groupedReplacementParts.NON_ESCO = groupedReplacementParts.NON_ESCO.filter(part => part.part_id !== deletedPartId);
    }
  };

  const listA = {
    "Serial_Number": getAssetAPIHook?.data?.bucket?.serial_number,
    "Machine_Number": getAssetAPIHook?.data?.bucket?.machine_number,
    "Make": getAssetAPIHook?.data?.bucket?.make
  };

  const listB = {
    "Stock_Number": getAssetAPIHook?.data?.bucket?.stock_number,
    "Type": getAssetAPIHook?.data?.bucket?.attachment_type,
    "Model": getAssetAPIHook?.data?.bucket?.model
  };

  const downloadDocs = async () => {
    const params: DownloadParamsType = {
      asset_id: selectedAsset.asset_id,
      file_path: selectedAsset.attachment?.url
    }
    const name = selectedAsset.attachment ? selectedAsset.attachment.name : "Asset Attachment";
    startDownload(assetsApi.downloadAssetAttachment(params), name);
  }

  const attachmentInfo = () => {
    return (
      <div className="attachment-info" data-testid="attachment-info">
        {selectedAsset.attachment == null ? <></> :
          <DownloadDocs
            buttonTitle={"Download"}
            docName={selectedAsset.attachment.name}
            onBtnClick={() => downloadDocs()}
            applyModalInternalGrid={true}
            modalType={"large"}
            canvasDeviceSize={'desktop'}
          />
        }
        <div className="replacement-parts-container">
          <div className="replacement-parts-item">
            <GroupFormField list={listA} />
          </div>
          <div className="replacement-parts-item">
            <GroupFormField list={listB} />
          </div>
        </div>
      </div>
    );
  };

  const displayAccordionDropdown = (data: ReplacementPartType[]) => {
    // Determine which handler to use based on data reference
    let handler: ((id: number) => void) | undefined;
    if (data === points) handler = handlePointsDeleted;
    else if (data === adapters) handler = handleAdaptersDeleted;
    else if (data === others) handler = handleOthersDeleted;
    else if (data === nonEsco) handler = handleNonEscoDeleted;
    else handler = undefined;
    return (
      <div className="adapters">
        {data.map((part: ReplacementPartType,) => (
          <Card
            key={part.asset_id}
            type={"replaceableParts"}
            cardInfo={part}
            setLoader={setIsAddingToList}
            renderToastMessage={renderToastMessage}
            isGuest={isGuest}
            onPartDeleted={handler}
          />
        ))}
      </div>
    );
  }

  const showAddBillOfMaterial = () => {
    if (setEditBill) {
      setEditBill(undefined);
    }
    setShowBillOfMaterial?.(true);
  }

  useEffect(() => {
    let accordianData = [
      {
        content: attachmentInfo(),
        title: t("rmation"),
      }
    ];
    const keys = Object.keys(groupedReplacementParts);
    if (keys.includes(POINTS)) {
      accordianData.push({
        content: displayAccordionDropdown(points),
        title: t("points"),
      });
    }
    if (keys.includes(ADAPTERS)) {
      accordianData.push({
        content: displayAccordionDropdown(adapters),
        title: t("adapters"),
      });
    }
    if (keys.includes(OTHERS)) {
      accordianData.push({
        content: displayAccordionDropdown(others),
        title: t("others"),
      });
    }
    // Always include Bill of Material accordion for non-esco
    if (selectedAsset.bucket_type && selectedAsset.bucket_type.toLowerCase() === 'non-esco') {
      const bom = groupReplacementParts(getAssetAPIHook?.data?.bucket?.replacement_parts).BILL_OF_MATERIALS;
      const billParts = bom?.map((part: ReplacementPartType) => (
        <Card
          key={part.asset_id}
          type={"replaceableParts"}
          cardInfo={part}
          nonEsco={true}
          editHandler={toggleBillOfMaterial}
          isGuest={isGuest}
          onPartDeleted={deletePartAPIHook.mutate}
        />
      )) || [];
      const billButton = (
        <div className="btn-container btn-margin" key="bill-add-btn">
          <Button
            label={t("addNewPart")}
            size={"sm"}
            variant={"secondary"}
            onClick={showAddBillOfMaterial}
            disabled={isGuest}
          />
        </div>
      );
      accordianData.push({
        content: (
          <div className="non-esco">
            {billParts}
            {billButton}
          </div>
        ),
        title: t("billOfMaterials")
      });
    }
    setAccordianContent(accordianData);
  }, [groupedReplacementParts]);



  return (
    <div
      className="replacement-parts"
      data-testid="replacement-parts"
    >
      {isLoading || isAddingToList || deletePartAPIHook.isPending || getAssetAPIHook?.isFetching ? (
          <LoadingSpinner darkMode={darkMode} />
      ) : null}
      <Accordion
        darkMode={darkMode}
        accordianContent={accordianContent}
        canvasDeviceSize={"none"}
      />
      {/* Bill of Material is now always included in accordianContent above */}
      {showToast ? (
        <div className="toast">
          <Notification
            darkMode={darkMode}
            title={toastData.variant == "success" ? "Success" : "Fail"}
            textContent={toastData.message}
            variant={toastData.variant}
            onClose={() => {
              setShowToast(false);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
